/**
 * CKEditor 5 Composable
 *
 * Provides a reusable pattern for integrating CKEditor 5 with Vue 3 after Vite 6 upgrade.
 * This composable handles the dynamic import and custom wrapper component creation needed
 * due to Vite 6's stricter ESM/CommonJS interop.
 *
 * @returns {Object} Object containing Ckeditor component, Editor class, and ready state
 *
 * @example
 * ```typescript
 * import { useCKEditor } from '@/composables/useCKEditor';
 *
 * const { Ckeditor, Editor, isEditorReady } = useCKEditor();
 * ```
 *
 * Template usage:
 * ```vue
 * <div v-if="Ckeditor && Editor" class="ckeditor-text">
 *   <component :is="Ckeditor" :editor="Editor" v-model="content" />
 * </div>
 * ```
 */

import { shallowRef, onMounted, onBeforeUnmount, watch, markRaw, ref, h } from 'vue';

export function useCKEditor() {
  // Use shallowRef to prevent Vue from creating deep reactive proxies
  const Ckeditor = shallowRef<any>(null);
  const Editor = shallowRef<any>(null);
  const isEditorReady = shallowRef(false);

  onMounted(async () => {
    // Load CKEditor asynchronously
    try {
      const [_ckEditorModule, editorModule] = await Promise.all([
        import("@ckeditor/ckeditor5-vue"),
        import("ckeditor5-custom-build"),
      ]);

      Editor.value = (editorModule as any).default || editorModule;

      // Custom wrapper component
      // We can't access the CKEditor Vue component directly from the UMD bundle,
      // so we create our own wrapper that replicates its functionality
      Ckeditor.value = {
        name: "ckeditor",
        props: {
          editor: { type: Function, required: true },
          modelValue: { type: String, default: "" },
          config: { type: Object, default: () => ({}) },
          disabled: { type: Boolean, default: false },
        },
        emits: ["update:modelValue", "ready"],
        setup(props: any, { emit }: any) {
          const editorInstance = shallowRef<any>(null);  // Use shallowRef
          const elRef = ref<HTMLElement | null>(null);

          onMounted(async () => {
            if (!elRef.value || !props.editor) return;

            const config = { ...props.config };
            if (props.modelValue) {
              config.initialData = props.modelValue;
            }

            try {
              const instance = await props.editor.create(elRef.value, config);
              editorInstance.value = markRaw(instance);  // Use markRaw to prevent proxy errors

              instance.model.document.on("change:data", () => {
                const data = instance.getData();
                emit("update:modelValue", data);
              });

              emit("ready", instance);
            } catch (error) {
              console.error("CKEditor initialization failed:", error);
            }
          });

          onBeforeUnmount(() => {
            if (editorInstance.value) {
              editorInstance.value.destroy();
            }
          });

          watch(
            () => props.modelValue,
            (newValue) => {
              if (editorInstance.value && newValue !== editorInstance.value.getData()) {
                editorInstance.value.setData(newValue);
              }
            }
          );

          watch(
            () => props.disabled,
            (isDisabled) => {
              if (!editorInstance.value) return;
              if (isDisabled) {
                editorInstance.value.enableReadOnlyMode("disabled");
              } else {
                editorInstance.value.disableReadOnlyMode("disabled");
              }
            }
          );

          return { elRef };
        },
        render() {
          return h('div', { ref: 'elRef' });  // Must use render function, not template string
        }
      };

      isEditorReady.value = true;
    } catch (error) {
      console.error("Failed to load CKEditor:", error);
    }
  });

  return {
    Ckeditor,
    Editor,
    isEditorReady
  };
}
